import {Button, Paper, Box, Divider, IconButton} from "@mui/material"
import {useSnackbar} from "notistack"
import {FC} from "react"
import {useGetApiQuery} from "../../services/goroskop"
import T from "../quiz/OnlyText"
import EnhancedTable from "./constituents/Table"
import EditIcon from '@mui/icons-material/Edit';
import Typography from '@mui/material/Typography';
import {Link} from 'react-router-dom';
import {getCookie} from '../../functions/Cookie';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import {Data} from '../../types/data';
import {normalizeDate} from '../../utils/date';

const Lines: FC = () => {
  const {data, error, isLoading} = useGetApiQuery('lines.php')
  const {enqueueSnackbar} = useSnackbar();

  const handleExportAll = () => {
    if (!data || data.length === 0) {
      enqueueSnackbar('Нет данных для экспорта', { variant: 'warning' });
      return;
    }

    const modStatuses = new Set(['1', '2', '3', '4', '5', '6', '7', '8', '9']);
    const allModRows = data.filter((obj) => modStatuses.has(String(obj.status)));

    if (allModRows.length === 0) {
      enqueueSnackbar('Нет анкет у модераторов 1-9 для экспорта', { variant: 'warning' });
      return;
    }

    const headers = [
      'ID',
      'Имя и фамилия',
      'Пол',
      'Дата рождения',
      'В год кого',
      'Знак зодиака',
      'Основной язык любви',
      'Второй язык любви',
      'VIP',
      'Телефон',
      'VK',
      'Контрольная дата контакта',
      'Раздел / Статус',
      'Дата регистрации',
      'Последнее изменение',
      'Источник',
      'Откуда узнали',
      'Отложено',
      'Город',
      'Email',
    ];

    const formatRowValues = (row: Data | any): (string | number)[] => [
      row.id ?? '',
      row.firstname || '',
      row.gender === '1' ? 'Мужской' : row.gender === '2' ? 'Женский' : row.gender || '',
      row.birthday || '',
      row.birthyear || '',
      row.zodiak || '',
      row.langlove || '',
      row.langlove2 || '',
      row.vip == '1' ? 'Да' : row.vip == '0' ? 'Нет' : (row.vip ?? ''),
      row.phone || '',
      row.vk || '',
      row.next_contact_date || '',
      `Мод_${row.status}`,
      row.reg_date || '',
      row.lastmodify || '',
      row.source_type || '',
      row.source || '',
      row.defer == '1' ? 'Да' : 'Нет',
      row.city || '',
      row.email || '',
    ];

    const cols = [
      { wch: 8 },  // ID
      { wch: 26 }, // Имя и фамилия
      { wch: 10 }, // Пол
      { wch: 14 }, // Дата рождения
      { wch: 14 }, // В год кого
      { wch: 14 }, // Знак зодиака
      { wch: 22 }, // Основной язык любви
      { wch: 22 }, // Второй язык любви
      { wch: 8 },  // VIP
      { wch: 18 }, // Телефон
      { wch: 28 }, // VK
      { wch: 26 }, // Контрольная дата контакта
      { wch: 18 }, // Раздел / Статус
      { wch: 18 }, // Дата регистрации
      { wch: 22 }, // Последнее изменение
      { wch: 16 }, // Источник
      { wch: 20 }, // Откуда узнали
      { wch: 10 }, // Отложено
      { wch: 18 }, // Город
      { wch: 24 }, // Email
    ];

    const modMap: Record<string, string> = {
      '1': '103',
      '2': '104',
      '3': '106',
      '4': '107',
      '5': '108',
      '6': '109',
      '7': '110',
      '8': '111',
      '9': '112',
    };

    const sortedData = [...allModRows].sort((a, b) => {
      const statusDiff = Number(a.status || 0) - Number(b.status || 0);
      if (statusDiff !== 0) return statusDiff;
      const aDate = normalizeDate(a.next_contact_date);
      const bDate = normalizeDate(b.next_contact_date);
      if (!aDate && !bDate) return Number(b.id || 0) - Number(a.id || 0);
      if (!aDate) return 1;
      if (!bDate) return -1;
      const dateDiff = aDate.localeCompare(bDate);
      if (dateDiff !== 0) return dateDiff;
      return Number(b.id || 0) - Number(a.id || 0);
    });

    const workbook = XLSX.utils.book_new();

    // 1. Общий объединенный лист с разделением по модераторам
    const mainAoaRows: (string | number)[][] = [headers];
    const mainMerges: XLSX.Range[] = [];
    let hasPreviousGroup = false;

    for (let i = 1; i <= 9; i++) {
      const modRows = sortedData.filter((row) => String(row.status) === String(i));
      if (modRows.length > 0) {
        if (hasPreviousGroup) {
          mainAoaRows.push([]); // Пустая строка-разделитель между таблицами модераторов
        }

        const modId = modMap[String(i)];
        const modProfile = data?.find((obj) => String(obj.id) === modId);
        const modName = modProfile?.firstname ? ` - ${modProfile.firstname}` : '';
        const modTitle = `Модератор ${i}${modName}`;

        const bannerText = `=== Откуда данные: Таблица «${modTitle}» (статус: Мод_${i}, анкет: ${modRows.length}) ===`;
        const bannerRowIndex = mainAoaRows.length;
        mainAoaRows.push([bannerText]);

        mainMerges.push({
          s: { r: bannerRowIndex, c: 0 },
          e: { r: bannerRowIndex, c: headers.length - 1 },
        });

        for (const row of modRows) {
          mainAoaRows.push(formatRowValues(row));
        }

        hasPreviousGroup = true;
      }
    }

    const mainSheet = XLSX.utils.aoa_to_sheet(mainAoaRows);
    mainSheet['!cols'] = cols;
    mainSheet['!merges'] = mainMerges;
    XLSX.utils.book_append_sheet(workbook, mainSheet, 'Все модераторы');

    // 2. Отдельные вкладки по каждому модератору (с 1 по 9)
    for (let i = 1; i <= 9; i++) {
      const modRows = sortedData.filter((row) => String(row.status) === String(i));
      if (modRows.length > 0) {
        const modId = modMap[String(i)];
        const modProfile = data?.find((obj) => String(obj.id) === modId);
        const tabAoaRows: (string | number)[][] = [
          headers,
          ...modRows.map(formatRowValues),
        ];
        const modSheet = XLSX.utils.aoa_to_sheet(tabAoaRows);
        modSheet['!cols'] = cols;
        const sheetTitle = `Модератор ${i}${modProfile?.firstname ? ` (${modProfile.firstname})` : ''}`
          .replace(/[:\\/?*\[\]]/g, '')
          .slice(0, 31);
        XLSX.utils.book_append_sheet(workbook, modSheet, sheetTitle);
      }
    }

    const dateStr = dayjs().format('YYYY-MM-DD_HH-mm');
    const fileName = `Линия_2_Модераторы_1-9_${dateStr}.xlsx`;

    XLSX.writeFile(workbook, fileName);

    enqueueSnackbar(`Экспортировано в Excel: все модераторы 1-9 (${sortedData.length} шт.)`, {
      variant: 'success',
    });
  };

  const role = String(getCookie('role'));

  function printExport(role: string) {
    if (role == 'ok_admin' || role == 'admin') {
      return (
        <Box sx={{ mt: 3, mb: 4, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <T>Экспортировать таблицу с 1 по 9 модератора:</T>
          <Box>
            <Button variant="contained" size="large" onClick={handleExportAll}>
              Экспортировать
            </Button>
          </Box>
        </Box>
      );
    }
    return null;
  }


  // let newDate = date ?
  //     data?.filter(obj => obj['next_contact_date'] && +new Date(obj['next_contact_date']) <= +new Date(date))
  //     : false
  return (
    <>
      {error && <h1>oops, error: {JSON.stringify(error)}</h1>}
      {/* <TextField
                id="date-picker"
                label='Выберите дату'
                type="date"
                sx={{ width: 220 }}
                InputLabelProps={{
                    shrink: true,
                }}
                value={date}
                onChange={(e) => dispastch(restrictDate(e.target.value))}
            /> */}
      {isLoading && <h1>Загрузка, подождите пожалуйста</h1>}
      {data && (
        <>
          {role == 'ok_admin' ?
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#ffffff',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Typography variant="subtitle1" sx={{fontWeight: 600, color: 'text.primary'}}>
                  Менеджер отдела обслуживания
                </Typography>
                <IconButton component={Link} to="../profile/569" size="small" color="primary">
                  <EditIcon fontSize="small"/>
                </IconButton>
              </Box>
              <Divider orientation="vertical" flexItem sx={{display: {xs: 'none', sm: 'block'}}}/>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Typography variant="subtitle1" sx={{fontWeight: 600, color: 'text.primary'}}>
                  Руководитель продаж
                </Typography>
                <IconButton component={Link} to="../profile/102" size="small" color="primary">
                  <EditIcon fontSize="small"/>
                </IconButton>
              </Box>
            </Paper>
            : null}
          <EnhancedTable data={data?.filter(obj => obj['status'] === '1')} nameOfTable='Модератор 1'
                         modInfo={data?.filter(obj => obj['id'] === '103')} exId={103}
                         defaultOrderBy='next_contact_date' defaultOrder='asc'/>
          <EnhancedTable data={data?.filter(obj => obj['status'] === '2')} nameOfTable='Модератор 2'
                         modInfo={data?.filter(obj => obj['id'] === '104')} exId={104}
                         defaultOrderBy='next_contact_date' defaultOrder='asc'/>
          <EnhancedTable data={data?.filter(obj => obj['status'] === '3')} nameOfTable='Модератор 3'
                         modInfo={data?.filter(obj => obj['id'] === '106')} exId={106}
                         defaultOrderBy='next_contact_date' defaultOrder='asc'/>
          <EnhancedTable data={data?.filter(obj => obj['status'] === '4')} nameOfTable='Модератор 4'
                         modInfo={data?.filter(obj => obj['id'] === '107')} exId={107}
                         defaultOrderBy='next_contact_date' defaultOrder='asc'/>
          <EnhancedTable data={data?.filter(obj => obj['status'] === '5')} nameOfTable='Модератор 5'
                         modInfo={data?.filter(obj => obj['id'] === '108')} exId={108}
                         defaultOrderBy='next_contact_date' defaultOrder='asc'/>
          <EnhancedTable data={data?.filter(obj => obj['status'] === '6')} nameOfTable='Модератор 6'
                         modInfo={data?.filter(obj => obj['id'] === '109')} exId={109}
                         defaultOrderBy='next_contact_date' defaultOrder='asc'/>
          <EnhancedTable data={data?.filter(obj => obj['status'] === '7')} nameOfTable='Модератор 7'
                         modInfo={data?.filter(obj => obj['id'] === '110')} exId={110}
                         defaultOrderBy='next_contact_date' defaultOrder='asc'/>
          <EnhancedTable data={data?.filter(obj => obj['status'] === '8')} nameOfTable='Модератор 8'
                         modInfo={data?.filter(obj => obj['id'] === '111')} exId={111}
                         defaultOrderBy='next_contact_date' defaultOrder='asc'/>
          <EnhancedTable data={data?.filter(obj => obj['status'] === '9')} nameOfTable='Модератор 9'
                         modInfo={data?.filter(obj => obj['id'] === '112')} exId={112}
                         defaultOrderBy='next_contact_date' defaultOrder='asc'/>
        </>
      )}

      {printExport(role)}
    </>
  )
}

export default Lines